import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuthContext } from '../contexts/AuthContext';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { HelpModal } from './HelpModal';
import { ScreenshotService, ScreenshotResult } from '../services/ScreenshotService';
import { useAlert } from '../contexts/AlertContext';
import { helpApiService } from '../services/helpApiService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faFileText, faUser, faDownload, faShieldAlt, faGem, faGraduationCap, faExclamationTriangle } from '@/icons';
import { useSiteOfflineEnabled } from '../contexts/SystemSettingsContext';
import { notificationApiService } from '../services/notificationApiService';
import { useWizardNavigation } from '../hooks/useWizardNavigation';
import { SaveConfirmationModal } from './SaveConfirmationModal';
import { SpinnerOverlay } from './SpinnerOverlay';
import { CustomTooltip } from './CustomTooltip';
import { TutorialOverlay } from './TutorialOverlay';
import { TutorialService } from '../services/tutorialService';
import { debugError } from '../utils/debug';
import { useFeatureFlag, useFeatureFlagWithLoading } from '../hooks/useFeatureFlag';
import { NotificationBellMenu } from './NotificationBellMenu';
import { ProFeatureTourModal } from './ProFeatureTourModal';
import { useProTrialSelfSignupOffer } from '../hooks/useProTrialSelfSignupOffer';
import { ProUpgradeModal } from './ProUpgradeModal';
import { TeamSwitcher, HeaderTeamSwitcher } from './TeamSwitcher';

/**
 * Navigation component for authenticated users
 */
export const Navigation: React.FC = () => {
    const { user, logout } = useAuthContext();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const siteOfflineEnabled = useSiteOfflineEnabled();
    const { addAlert } = useAlert();
    const { enabled: notificationsEnabled, ready: notificationsFlagReady } = useFeatureFlagWithLoading('Notifications');
    const continuingEducationEnabled = useFeatureFlag('ContinuingEducation');
    const proFeatureTourEnabled = useFeatureFlag('ProFeatureTour');
    // The trial offer takes the Pro features button's place, and only when that button is not
    // there: two calls to action in the same slot would compete, and the tour already leads to
    // the upgrade modal where the trial is offered anyway. canOffer is the server's verdict --
    // false for a team already on Pro, already on or lapsed from a trial, or one that has used
    // its single self-started trial.
    const trialOffer = useProTrialSelfSignupOffer();
    const showTrialCta = !proFeatureTourEnabled && trialOffer.canOffer;
    const location = useLocation();
    const {
        navigateTo,
        showSaveModal,
        isSaving,
        handleSave,
        handleDiscard,
        handleCancel,
        checkAction
    } = useWizardNavigation();
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [showProFeatureTour, setShowProFeatureTour] = useState(false);
    const [showProUpgradeFromTour, setShowProUpgradeFromTour] = useState(false);
    const [showTrialFromNav, setShowTrialFromNav] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [screenshot, setScreenshot] = useState<ScreenshotResult | undefined>();
    const [isSendingHelp, setIsSendingHelp] = useState(false);
    const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
    const [isNavigatingToAdmin, setIsNavigatingToAdmin] = useState(false);
    const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
    const [showLogoTooltip, setShowLogoTooltip] = useState(false);
    const [showCasesTooltip, setShowCasesTooltip] = useState(false);
    const [showHelpTooltip, setShowHelpTooltip] = useState(false);
    const [showProfileTooltip, setShowProfileTooltip] = useState(false);
    const [showNotificationTutorial, setShowNotificationTutorial] = useState(false);
    const [notificationTutorialTarget, setNotificationTutorialTarget] = useState<HTMLElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    let subscriptionType = user?.team?.subscriptionType;
    if (user?.role === 'Admin' || user?.role === 'Root') {
        try {
            const override = sessionStorage.getItem('subscription_type_override');
            if (override) subscriptionType = override;
        } catch {
            // Ignore
        }
    }
    const isProSubscription = (subscriptionType || 'Standard').toLowerCase() === 'pro';

    const handleLogout = async () => {
        try {
            // logout() clears auth state directly (no react-router navigation) — once
            // isAuthenticated flips false, ProtectedRoute swaps SimpleCaseProvider out for a
            // redirect, unmounting it exactly like leaving the wizard would. Route it through
            // the same unsaved-changes gate as in-app navigation so it saves/prompts first
            // instead of silently discarding edits (checkAction closes the dropdown itself via
            // setShowUserDropdown below only once logout actually runs, not if the user cancels).
            await checkAction(async () => {
                await logout();
                setShowUserDropdown(false);
            });
        } catch (error) {
            debugError('Logout failed:', error);
        }
    };

    // Hide admin spinner when we're on the admin page
    useEffect(() => {
        if (location.pathname.startsWith('/admin')) {
            setIsNavigatingToAdmin(false);
        }
    }, [location.pathname]);

    // In Manual Save mode (or any forceSave failure), checkNavigation shows
    // SaveConfirmationModal and defers navigation instead of navigating immediately —
    // location.pathname never changes until the user chooses Save/Discard, so the
    // pathname-based effect above never fires. Without this, the admin spinner overlay
    // would render on top of the modal forever, blocking its buttons. The modal itself
    // is the loading/confirmation UI at that point, so hide the spinner behind it.
    useEffect(() => {
        if (showSaveModal) {
            setIsNavigatingToAdmin(false);
        }
    }, [showSaveModal]);

    const toggleUserDropdown = () => {
        setShowUserDropdown(!showUserDropdown);
    };

    const closeUserDropdown = () => {
        setShowUserDropdown(false);
    };

    const handleNeedHelp = async () => {
        try {
            // Show modal immediately with loading state
            setIsCapturingScreenshot(true);
            setShowHelpModal(true);

            // Capture screenshot
            const screenshotResult = await ScreenshotService.capturePage();
            setScreenshot(screenshotResult);
        } catch (error) {
            debugError('Error capturing screenshot:', error);
            addAlert('error', 'Failed to capture screenshot. Opening help modal without screenshot.');
            setScreenshot(undefined);
        } finally {
            setIsCapturingScreenshot(false);
        }
    };

    const handleSendHelp = async (message: string, includeScreenshot: boolean, screenshotData?: ScreenshotResult) => {
        try {
            setIsSendingHelp(true);

            const response = await helpApiService.sendHelpRequest(message, includeScreenshot, screenshotData);

            if (response.success) {
                addAlert('success', 'Help request sent successfully! We\'ll get back to you soon.');
                setShowHelpModal(false);
                setScreenshot(undefined);
            } else {
                addAlert('error', response.message || 'Failed to send help request. Please try again.');
            }
        } catch (error) {
            debugError('Error sending help request:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to send help request. Please try again.';
            addAlert('error', errorMessage);
        } finally {
            setIsSendingHelp(false);
        }
    };

    const handleCloseHelpModal = () => {
        setShowHelpModal(false);
        setScreenshot(undefined);
        setIsCapturingScreenshot(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowUserDropdown(false);
            }
        };

        if (showUserDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserDropdown]);

    // Poll unread notification count
    useEffect(() => {
        if (!notificationsEnabled) return;

        const fetchUnreadCount = async () => {
            try {
                const response = await notificationApiService.getUnreadCount();
                if (response.success) {
                    setUnreadNotificationCount(response.unreadCount ?? 0);
                }
            } catch {
                // Silently fail - notification count is not critical
            }
        };

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60000);

        const handleNotificationRead = () => fetchUnreadCount();
        window.addEventListener('notification-read', handleNotificationRead);

        return () => {
            clearInterval(interval);
            window.removeEventListener('notification-read', handleNotificationRead);
        };
    }, [notificationsEnabled]);

    // Notifications tutorial. Gated on notificationsFlagReady (not just notificationsEnabled)
    // and depends on both, so this re-evaluates once feature flags actually finish loading —
    // an empty dependency array here would silently never arm the tutorial for any user whose
    // flags hadn't resolved by the moment this effect first ran. See docs/FEATURE_FLAGS_GUIDE.md
    // "Avoiding the mount-effect loading race".
    useEffect(() => {
        if (!notificationsFlagReady) return;
        if (notificationsEnabled && !TutorialService.hasDisplayedNotificationsTutorial()) {
            const timer = setTimeout(() => {
                const bellButton = document.querySelector('[data-tutorial-target="notification-bell"]') as HTMLElement;
                if (bellButton) {
                    setNotificationTutorialTarget(bellButton);
                    setShowNotificationTutorial(true);
                }
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, [notificationsEnabled, notificationsFlagReady]);

    const handleNotificationTutorialClose = () => {
        setShowNotificationTutorial(false);
        setNotificationTutorialTarget(null);
        TutorialService.markNotificationsTutorialAsDisplayed();
    };

    return (
        <nav style={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: theme.colors.surface,
            borderBottom: `1px solid ${theme.colors.border.default}`,
            boxShadow: theme.shadows.md,
        }}>
            {siteOfflineEnabled && (
                <div style={{
                    backgroundColor: '#dc2626',
                    color: '#ffffff',
                    textAlign: 'center',
                    padding: '10px 16px',
                    fontSize: '14px',
                    fontWeight: 700,
                }}>
                    <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '8px' }} />
                    Site Offline Temporarily - Try Again In A Few Minutes
                </div>
            )}
            <div className="rw-wrap rw-nav-bar" style={{
                maxWidth: '1400px',
                margin: '0 auto',
                paddingLeft: theme.spacing.lg,
                paddingRight: theme.spacing.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                // min-height rather than height: on a phone the two groups wrap onto separate
                // lines, and a fixed height would make them overlap instead of growing the bar.
                minHeight: '64px',
                position: 'relative',
            }}>
                <div className="rw-min0" style={{ display: 'flex', alignItems: 'center' }}>
                    <CustomTooltip text="Home" position="bottom" show={showLogoTooltip}>
                        <button
                            onClick={() => navigateTo('/')}
                            onMouseEnter={() => setShowLogoTooltip(true)}
                            onMouseLeave={() => setShowLogoTooltip(false)}
                            style={{
                                textDecoration: 'none',
                                color: 'inherit',
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                            }}
                        >
                            {/* Below desktop the wordmark is the first thing worth losing: the icon
                                alone still identifies the app and buys back ~120px of a crowded
                                bar. logo-mark.svg is the same glyph as logo-name.svg with the
                                lettering removed, so the two are visually identical where they
                                overlap. */}
                            <img
                                className="rw-hide-compact"
                                src="/logo-name.svg"
                                alt="Calculist"
                                style={{ height: '32px', width: 'auto' }}
                            />
                            <img
                                className="rw-compact-only"
                                src="/logo-mark.svg"
                                alt="Calculist"
                                style={{ height: '32px', width: 'auto' }}
                            />
                        </button>
                    </CustomTooltip>
                    <CustomTooltip text="Cases" position="bottom" show={showCasesTooltip}>
                        <button
                            className="rw-hide-compact"
                            onClick={() => navigateTo('/')}
                            onMouseEnter={() => setShowCasesTooltip(true)}
                            onMouseLeave={() => setShowCasesTooltip(false)}
                            style={{
                                marginLeft: theme.spacing.lg,
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                backgroundColor: theme.colors.primary,
                                color: theme.colors.text.inverse,
                                textDecoration: 'none',
                                borderRadius: theme.borderRadius.md,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.semibold,
                                transition: `background-color ${theme.transitions.normal}`,
                                border: 'none',
                                cursor: 'pointer',
                            }}
                        >
                            CASES
                        </button>
                    </CustomTooltip>
                    <HeaderTeamSwitcher />
                </div>

                {proFeatureTourEnabled && !isProSubscription && (
                    <button
                        className="rw-hide-phone"
                        onClick={() => setShowProFeatureTour(true)}
                        style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            backgroundColor: theme.colors.secondary,
                            border: 'none',
                            borderRadius: theme.borderRadius.md,
                            cursor: 'pointer',
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.bold,
                            color: theme.colors.text.inverse,
                            transition: `background-color ${theme.transitions.normal}`,
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = theme.colors.secondary; }}
                    >
                        <FontAwesomeIcon icon={faGem} style={{ fontSize: theme.typography.fontSize.sm }} />
                        PRO FEATURES
                    </button>
                )}

                {showTrialCta && !isProSubscription && (
                    <button
                        className="rw-nav-cta"
                        onClick={() => setShowTrialFromNav(true)}
                        style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing.sm,
                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                            backgroundColor: theme.colors.secondary,
                            border: 'none',
                            borderRadius: theme.borderRadius.md,
                            cursor: 'pointer',
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.bold,
                            color: theme.colors.text.inverse,
                            transition: `background-color ${theme.transitions.normal}`,
                            whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.primary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = theme.colors.secondary; }}
                    >
                        <FontAwesomeIcon icon={faGem} style={{ fontSize: theme.typography.fontSize.sm, flexShrink: 0 }} />
                        {/* Kept at every width, unlike the Pro features button it replaces: this
                            is the only place a smaller screen is offered the trial at all. The
                            label shortens instead of the button disappearing. */}
                        <span className="rw-hide-compact">Start Your Free Pro Trial!</span>
                        <span className="rw-compact-only">Pro Trial</span>
                    </button>
                )}

                <div className="rw-wrap rw-min0 rw-nav-actions" style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.lg }}>
                    <CustomTooltip text="Contact support" position="bottom" show={showHelpTooltip}>
                        <button
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.sm,
                                backgroundColor: theme.colors.surface,
                                border: 'none',
                                borderRadius: theme.borderRadius.md,
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                cursor: 'pointer',
                                transition: `background-color ${theme.transitions.normal}`,
                            }}
                            onClick={handleNeedHelp}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary;
                                setShowHelpTooltip(true);
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = theme.colors.surface;
                                setShowHelpTooltip(false);
                            }}
                        >
                            <i className="fas fa-headset" style={{
                                fontSize: theme.typography.fontSize.md,
                                color: theme.colors.primary,
                            }}></i>
                            <span style={{
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.bold,
                                color: theme.colors.primary,
                            }}>
                                <span className="rw-hide-phone">NEED HELP?</span>
                            </span>
                        </button>
                    </CustomTooltip>

                    {notificationsEnabled && (
                        <NotificationBellMenu unreadNotificationCount={unreadNotificationCount} />
                    )}

                    <div ref={dropdownRef} style={{ position: 'relative' }}>
                        <CustomTooltip text="Profile" position="bottom" show={showProfileTooltip && !showUserDropdown}>
                            <button
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    borderRadius: theme.borderRadius.sm,
                                    transition: `all ${theme.transitions.normal}`,
                                }}
                                onClick={toggleUserDropdown}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = theme.colors.primary + '10';
                                    setShowProfileTooltip(true);
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    setShowProfileTooltip(false);
                                }}
                            >
                            {isProSubscription && (
                                <FontAwesomeIcon
                                    icon={faGem}
                                    style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.primary,
                                    }}
                                />
                            )}
                            <FontAwesomeIcon
                                icon={faUser}
                                style={{
                                    fontSize: theme.typography.fontSize.lg,
                                    color: theme.colors.text.primary,
                                }}
                            />
                            <span style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.primary,
                                fontWeight: theme.typography.fontWeight.medium,
                            }}>
                                <span className="rw-hide-phone">{user?.firstName || 'User'}</span>
                            </span>
                            <span style={{
                                fontSize: theme.typography.fontSize.xs,
                                color: theme.colors.text.primary,
                            }}>▼</span>
                            </button>
                        </CustomTooltip>

                        {showUserDropdown && (
                            <div style={{
                                position: 'absolute',
                                top: '100%',
                                right: '0',
                                backgroundColor: theme.colors.surface,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.md,
                                boxShadow: theme.shadows.md,
                                minWidth: '200px',
                                zIndex: 1000,
                                marginTop: theme.spacing.xs,
                            }}>
                                {/* User Information Section */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: theme.spacing.sm,
                                    padding: `${theme.spacing.md} ${theme.spacing.md}`,
                                    borderBottom: `1px solid ${theme.colors.border.default}`,
                                    backgroundColor: theme.colors.background,
                                }}>
                                    <FontAwesomeIcon
                                        icon={faUser}
                                        style={{
                                            fontSize: theme.typography.fontSize.lg,
                                            color: theme.colors.primary,
                                        }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: theme.typography.fontSize.sm,
                                            fontWeight: theme.typography.fontWeight.bold,
                                            color: theme.colors.text.primary,
                                            marginBottom: theme.spacing.xs,
                                        }}>
                                            {user?.firstName} {user?.lastName}
                                        </div>
                                        <div style={{
                                            fontSize: theme.typography.fontSize.xs,
                                            color: theme.colors.text.secondary,
                                            marginBottom: theme.spacing.xs,
                                        }}>
                                            {user?.emailAddress}
                                        </div>
                                        <div style={{
                                            fontSize: theme.typography.fontSize.xs,
                                            color: theme.colors.text.muted,
                                            textTransform: 'capitalize',
                                        }}>
                                            {user?.businessRole}
                                        </div>
                                        {user?.team?.name && (
                                            <div style={{
                                                fontSize: theme.typography.fontSize.xs,
                                                color: theme.colors.text.secondary,
                                                marginTop: theme.spacing.xs,
                                            }}>
                                                {user.team.name}
                                            </div>
                                        )}
                                        <TeamSwitcher />
                                        <div style={{
                                            fontSize: theme.typography.fontSize.xs,
                                            color: isProSubscription ? theme.colors.text.inverse : theme.colors.text.muted,
                                            backgroundColor: isProSubscription ? theme.colors.primary : 'transparent',
                                            padding: isProSubscription ? `${theme.spacing.xs} ${theme.spacing.sm}` : 0,
                                            borderRadius: isProSubscription ? theme.borderRadius.sm : 0,
                                            textTransform: 'capitalize',
                                            marginTop: theme.spacing.xs,
                                            display: isProSubscription ? 'inline-flex' : 'inline-block',
                                            alignItems: 'center',
                                            gap: isProSubscription ? theme.spacing.xs : 0,
                                        }}>
                                            {isProSubscription && (
                                                <FontAwesomeIcon
                                                    icon={faGem}
                                                    style={{
                                                        fontSize: theme.typography.fontSize.xs,
                                                        color: theme.colors.text.inverse,
                                                    }}
                                                />
                                            )}
                                            {`${subscriptionType || 'Standard'} subscription`}
                                        </div>
                                        {(user?.role === 'Admin' || user?.role === 'Root') && (
                                            <div style={{
                                                fontSize: theme.typography.fontSize.xs,
                                                color: theme.colors.text.primary,
                                                fontWeight: theme.typography.fontWeight.bold,
                                                textTransform: 'capitalize',
                                                marginTop: theme.spacing.xs,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: theme.spacing.xs,
                                            }}>
                                                <FontAwesomeIcon
                                                    icon={faShieldAlt}
                                                    style={{
                                                        fontSize: theme.typography.fontSize.xs,
                                                        color: theme.colors.text.primary,
                                                    }}
                                                />
                                                {user?.role}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    onClick={() => {
                                        closeUserDropdown();
                                        navigateTo('/downloads');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                        width: '100%',
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: `all ${theme.transitions.normal}`,
                                        textDecoration: 'none',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                                        e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = theme.colors.primary;
                                    }}
                                >
                                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: '14px' }} />
                                    DOWNLOADS
                                </button>
                                {continuingEducationEnabled && (
                                    <button
                                        onClick={() => {
                                            closeUserDropdown();
                                            navigateTo('/continuing-education');
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: theme.spacing.sm,
                                            width: '100%',
                                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            fontSize: theme.typography.fontSize.sm,
                                            fontWeight: theme.typography.fontWeight.medium,
                                            color: theme.colors.primary,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: `all ${theme.transitions.normal}`,
                                            textDecoration: 'none',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.backgroundColor = theme.colors.primary;
                                            e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = theme.colors.primary;
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faGraduationCap} style={{ fontSize: '14px' }} />
                                        CONTINUING EDUCATION
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        closeUserDropdown();
                                        navigateTo('/settings');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                        width: '100%',
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: `all ${theme.transitions.normal}`,
                                        textDecoration: 'none',
                                    }}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                                        e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = theme.colors.primary;
                                    }}
                                >
                                    <FontAwesomeIcon icon={faCog} style={{ fontSize: '14px' }} />
                                    SETTINGS
                                </button>
                                {/* <Link
                                    to="/terms_and_conditions"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                        width: '100%',
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: `background-color ${theme.transitions.normal}`,
                                        textDecoration: 'none',
                                    }}
                                    onClick={closeUserDropdown}
                                >
                                    <FontAwesomeIcon icon={faFileText} style={{ fontSize: '14px' }} />
                                    TERMS AND CONDITIONS
                                </Link> */}
                                <div style={{
                                    height: '1px',
                                    backgroundColor: theme.colors.border.default,
                                    margin: `${theme.spacing.xs} 0`,
                                }}></div>
                                {(user?.role === 'Admin' || user?.role === 'Root') && (
                                    <button
                                        onClick={() => {
                                            closeUserDropdown();
                                            setIsNavigatingToAdmin(true);
                                            navigateTo('/admin');
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: theme.spacing.sm,
                                            width: '100%',
                                            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            fontSize: theme.typography.fontSize.sm,
                                            fontWeight: theme.typography.fontWeight.medium,
                                            color: theme.colors.primary,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: `all ${theme.transitions.normal}`,
                                            textDecoration: 'none',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.backgroundColor = theme.colors.primary;
                                            e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.color = theme.colors.primary;
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '14px' }} />
                                        ADMIN
                                    </button>
                                )}
                                {(user?.role === 'Admin' || user?.role === 'Root') && (
                                    <div style={{
                                        height: '1px',
                                        backgroundColor: theme.colors.border.default,
                                        margin: `${theme.spacing.xs} 0`,
                                    }}></div>
                                )}
                                <button
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: theme.spacing.sm,
                                        width: '100%',
                                        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                        backgroundColor: 'transparent',
                                        border: 'none',
                                        fontSize: theme.typography.fontSize.sm,
                                        fontWeight: theme.typography.fontWeight.medium,
                                        color: theme.colors.primary,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: `all ${theme.transitions.normal}`,
                                        textDecoration: 'none',
                                    }}
                                    onClick={handleLogout}
                                    onMouseEnter={e => {
                                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                                        e.currentTarget.style.color = theme.colors.text.inverse || '#fff';
                                    }}
                                    onMouseLeave={e => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = theme.colors.primary;
                                    }}
                                >
                                    <i className="fas fa-sign-out-alt" style={{
                                        fontSize: theme.typography.fontSize.sm,
                                        marginRight: theme.spacing.xs,
                                    }}></i>
                                    LOGOUT
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Pro Feature Tour Modal */}
            <ProFeatureTourModal
                isOpen={showProFeatureTour}
                onClose={() => setShowProFeatureTour(false)}
                onUpgradeClick={() => setShowProUpgradeFromTour(true)}
            />

            <ProUpgradeModal
                isOpen={showProUpgradeFromTour}
                onClose={() => setShowProUpgradeFromTour(false)}
                featureDescription=""
                generic
            />

            {/* Straight to the trial view -- the button promised a trial, not a payment flow. */}
            <ProUpgradeModal
                isOpen={showTrialFromNav}
                onClose={() => setShowTrialFromNav(false)}
                featureDescription=""
                generic
                openToTrial
            />

            {/* Help Modal */}
            <HelpModal
                isOpen={showHelpModal}
                onClose={handleCloseHelpModal}
                onSend={handleSendHelp}
                screenshot={screenshot}
                isSending={isSendingHelp}
                isCapturingScreenshot={isCapturingScreenshot}
            />

            {/* Save Confirmation Modal */}
            <SaveConfirmationModal
                isVisible={showSaveModal}
                onSave={handleSave}
                onDiscard={handleDiscard}
                onCancel={handleCancel}
                isSaving={isSaving}
            />

            {/* Admin Navigation Spinner */}
            <SpinnerOverlay isVisible={isNavigatingToAdmin} message="Solving the world's problems..." />

            {/* Notifications Tutorial */}
            <TutorialOverlay
                isVisible={showNotificationTutorial}
                targetElement={notificationTutorialTarget}
                title="Notifications"
                description="You now have in-app notifications! Click the bell icon to view your notifications. A red badge will appear when you have unread items."
                onClose={handleNotificationTutorialClose}
                onSkip={handleNotificationTutorialClose}
                currentStep={1}
                totalSteps={1}
                position="bottom"
            />
        </nav>
    );
};

